import { ColumnDef } from '@tanstack/react-table';
import { DataTableColumnHeader, StatusChip } from '@/components/core';
import { formatAttendanceDay } from '@/modules/attendance/hr-my-attendance/services/attendance-rules';
import { OvertimeLedgerEntry } from '../../types/overtime.types';
import { formatSecondsAsHoursMinutes, formatSignedHoursMinutes } from '../../utils/format-duration';

/** Ledger `Source` → status text (legacy user-overtime-ledger-table statusTextMap). */
const SOURCE_LABEL_KEYS: Record<string, string> = {
  ClockOut: 'CLOCK_OUT',
  CorrectionRequestApproved: 'CORRECTION_APPROVED',
  Import: 'IMPORTED',
  LeaveRequestPending: 'LEAVE_PENDING',
  LeaveRequestApproved: 'LEAVE_APPROVED',
  LeaveRequestRejected: 'LEAVE_REJECTED',
  PaidOff: 'PAID_OFF',
  DataBalance: 'DATA_BALANCED',
  DataMigration: 'DATA_MIGRATION',
};

export const createOvertimeLedgerColumns = ({
  t,
}: {
  t: (key: string) => string;
}): ColumnDef<OvertimeLedgerEntry>[] => [
  {
    id: 'AttendanceDate',
    meta: { filter: { type: 'date' } },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('DATE')} />,
    accessorFn: (row) => row.AttendanceDate,
    // A date-only day: shown as the day it names, never converted.
    cell: ({ row }) => formatAttendanceDay(row.original.AttendanceDate),
  },
  {
    id: 'TotalWorkingTimePerDay',
    header: ({ column }) => (
      <DataTableColumnHeader column={column} title={t('TOTAL_WORKING_HOURS')} />
    ),
    accessorFn: (row) => row.TotalWorkingTimePerDay,
    cell: ({ row }) =>
      row.original.TotalWorkingTimePerDay
        ? formatSecondsAsHoursMinutes(row.original.TotalWorkingTimePerDay)
        : '-',
  },
  {
    id: 'OverTime',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('OVERTIME_HOURS')} />,
    accessorFn: (row) => row.OverTime,
    cell: ({ row }) => formatSignedHoursMinutes(row.original.OverTime),
  },
  {
    id: 'CumulativeOverTime',
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('CUMULATIVE_HOURS')} />,
    accessorFn: (row) => row.CumulativeOverTime,
    cell: ({ row }) => formatSignedHoursMinutes(row.original.CumulativeOverTime),
  },
  {
    id: 'Source',
    meta: {
      filter: {
        type: 'options',
        options: Object.entries(SOURCE_LABEL_KEYS).map(([value, key]) => ({
          value,
          label: t(key),
        })),
        // Nine statuses: one dropdown instead of a wall of checkboxes.
        dropdown: true,
      },
    },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('STATUS')} />,
    accessorFn: (row) => row.Source,
    cell: ({ row }) => {
      const key = SOURCE_LABEL_KEYS[row.original.Source];
      return <StatusChip tone="muted">{key ? t(key) : row.original.Source}</StatusChip>;
    },
  },
  {
    id: 'Note',
    // Card: in the footer, as in the old portal.
    meta: { filter: { type: 'text' }, mobile: 'footer' },
    header: ({ column }) => <DataTableColumnHeader column={column} title={t('NOTE')} />,
    accessorFn: (row) => row.Note,
    cell: ({ row }) => row.original.Note?.trim() || '-',
  },
];
